import { Box, Image, Stack, useMediaQuery } from "@chakra-ui/react";
import { Container } from "components";
import { InternalLink } from "components";
import { route } from "config/routes";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
} from "@chakra-ui/react";
import {
  ILegalArticleLink,
  ILegalMenu,
  ILegalMenuSection,
} from "interfaces/legal-guide-interfaces";
import { useRouter } from "next/router";
import { theme } from "theme";
import { useRef, useEffect, useState } from "react";

interface ILegalNavigationSectionProps {
  section: ILegalMenuSection;
}

interface ILegalNavigationLinkProps {
  link: ILegalArticleLink;
}

interface ILayoutLegalsProps {
  children: any;
  menu: ILegalMenu;
}

const LegalMobileMenu = ({ sections }) => {
  const nameRef = useRef("Articles");

  return (
    <Menu>
      <MenuButton background={"transparent"} color={"primary.500"}>
        {nameRef.current}
        <ChevronDownIcon ml={2} />
      </MenuButton>
      <MenuList>
        {sections.map((section) => {
          return (
            <MenuGroup title={section.name}>
              {section.links.map((link: ILegalArticleLink) => {
                return (
                  <InternalLink href={route("LEGAL_INDEX", link.slug)}>
                    <MenuItem
                      onClick={() => {
                        return (nameRef.current =
                          link.name.length > 10
                            ? `${link.name.slice(0, 8)}...`
                            : link.name);
                      }}
                      fontSize={"sm"}
                    >
                      {link.name}
                    </MenuItem>
                  </InternalLink>
                );
              })}
            </MenuGroup>
          );
        })}
      </MenuList>
    </Menu>
  );
};

const LegalNavigationLink = ({ link }: ILegalNavigationLinkProps) => {
  const { name, slug } = link;

  const {
    query: { legalSlug },
  } = useRouter();

  const isActive = legalSlug === slug ? true : false;

  return (
    <InternalLink href={route("LEGAL_INDEX", slug)}>
      <Box
        lineHeight="none"
        rounded="lg"
        color={isActive ? "primary.600" : "gray.600"}
        _hover={{
          color: "primary.600",
        }}
      >
        {name}
      </Box>
    </InternalLink>
  );
};

const LegalNavigationSection = ({ section }: ILegalNavigationSectionProps) => {
  return (
    <Box>
      <Box
        mb={3}
        fontWeight={600}
        textTransform="uppercase"
        fontSize="xs"
        color="gray.700"
        letterSpacing="wide"
      >
        {section.name}
      </Box>
      {section.links && (
        <Stack spacing={4}>
          {section.links.map((link: ILegalArticleLink) => {
            return <LegalNavigationLink key={link.id} link={link} />;
          })}
        </Stack>
      )}
    </Box>
  );
};

export const LayoutLegals = ({ children, menu }: ILayoutLegalsProps) => {
  const [isLargerThanLg, setIsLargerThanLg] = useState(true);
  const [largeSizeQuery] = useMediaQuery("(min-width : 62rem)");

  useEffect(() => {
    setIsLargerThanLg(largeSizeQuery);
  }, [largeSizeQuery]);

  return (
    <Box>
      <Box
        h={20}
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.150"
        w="100%"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container display="flex" alignItems="center" height="100%">
          <InternalLink href={route("HOMEPAGE_INDEX")}>
            <Box w={{ base: 160, lg: 160 }}>
              <Image src="/images/logo/main.png" />
            </Box>
          </InternalLink>
          <Box ml="auto" fontSize="sm">
            {isLargerThanLg ? (
              <Box>
                Need help?{" "}
                <InternalLink href={route("CONTACT_INDEX")}>
                  <Box
                    as="span"
                    color="primary.600"
                    _hover={{
                      textDecoration: "underline",
                    }}
                  >
                    Contact support
                  </Box>
                </InternalLink>
              </Box>
            ) : (
              <LegalMobileMenu sections={menu.sections} />
            )}
          </Box>
        </Container>
      </Box>

      <Container display="flex">
        <Box
          w={isLargerThanLg ? 200 : 0}
          height={`calc(100vh - ${theme.sizes[20]})`}
          position="sticky"
          visibility={isLargerThanLg ? "visible" : "hidden"}
          top={20}
          py={10}
          pl={0}
          fontSize="sm"
        >
          <Stack spacing={10}>
            {menu.sections.map((section) => {
              return (
                <LegalNavigationSection key={section.id} section={section} />
              );
            })}
          </Stack>
        </Box>
        <Box flex={1}>{children}</Box>
      </Container>
    </Box>
  );
};
